import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheckCircle } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    urgency: 'normal'
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would normally send the data to your backend
    console.log('Contact form submitted:', formData);
    setIsSubmitted(true);
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: "24/7 Care Line",
      details: "03333 394 077",
      description: "Available 24 hours a day, 7 days a week for urgent care needs and emergencies.",
      action: "tel:03333394077"
    },
    {
      icon: FiMail,
      title: "Email Us",
      details: "enquiries@odaracare.org.uk",
      description: "Send us an email and we'll respond within 24 hours during business days.",
      action: "mailto:enquiries@odaracare.org.uk"
    },
    {
      icon: FiMapPin,
      title: "HQ",
      details: "86-90 Paul Street, London, EC2A 4NE",
      description: "Our main office is open Monday to Friday, 9am to 5pm for in-person consultations.",
      action: "https://maps.google.com"
    }
  ];

  const officeHours = [
    { day: "Monday - Friday", hours: "9:00 AM - 5:00 PM" },
    { day: "Saturday", hours: "10:00 AM - 2:00 PM" },
    { day: "Sunday", hours: "Emergency calls only" },
    { day: "Bank Holidays", hours: "Emergency calls only" }
  ];

  const emergencyInfo = [
    {
      title: "Medical Emergency",
      description: "Call 999 immediately for life-threatening emergencies"
    },
    {
      title: "Urgent Care Needs",
      description: "Call our 24/7 care line: 03333 394 077"
    },
    {
      title: "Non-Urgent Enquiries",
      description: "Use the contact form or email us during business hours"
    }
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center border border-primary-100"
        >
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-primary-900" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Message Sent Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for contacting us. We've received your message and will respond within 24 hours during business days.
          </p>
          <p className="text-sm text-gray-600">
            For urgent matters, please call our 24/7 care line: <strong>03333 394 077</strong>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Odara Care - Get in Touch | 24/7 Care Support</title>
        <meta name="description" content="Contact Odara Care for professional home care services. 24/7 support available. Call 03333 394 077 or use our contact form for care enquiries." />
        <meta name="keywords" content="contact odara care, care enquiries, 24/7 support, home care contact, healthcare support" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 to-secondary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Get in <span className="text-primary-900">Touch</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're here to help with all your care needs. Whether you have questions about our services or need immediate support, our team is ready to assist you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Emergency Notice */}
      <section className="bg-red-50 border-l-4 border-red-500 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <SafeIcon icon={FiPhone} className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-red-800">
                <strong>Emergency?</strong> For life-threatening emergencies, call 999. For urgent care needs, call our 24/7 line: <strong>03333 394 077</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-50 to-secondary-50 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300 border border-primary-100"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-6">
                  <SafeIcon icon={info.icon} className="w-6 h-6 text-primary-900" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{info.title}</h3>
                <p className="text-lg font-medium text-primary-900 mb-3">{info.details}</p>
                <p className="text-gray-600 mb-6">{info.description}</p>
                <a
                  href={info.action}
                  className="inline-flex items-center text-primary-900 hover:text-primary-700 font-medium group"
                >
                  Contact Now
                  <SafeIcon icon={FiSend} className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Office Hours */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg border border-primary-100"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Urgency</label>
                    <select
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                      <option value="emergency">Emergency</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Please describe your care needs or enquiry..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary-900 text-white px-6 py-4 rounded-lg font-semibold hover:bg-primary-800 transition-colors duration-200 flex items-center justify-center"
                >
                  <SafeIcon icon={FiSend} className="mr-2 w-5 h-5" />
                  Send Message
                </button>
              </form>
            </motion.div>

            {/* Office Hours and Additional Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Office Hours */}
              <div className="bg-white p-8 rounded-xl shadow-lg border border-primary-100">
                <div className="flex items-center space-x-3 mb-6">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-primary-900" />
                  <h3 className="text-2xl font-bold text-gray-900">Office Hours</h3>
                </div>
                <div className="space-y-4">
                  {officeHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-primary-100 last:border-b-0">
                      <span className="font-medium text-gray-900">{schedule.day}</span>
                      <span className="text-gray-600">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-100">
                  <p className="text-sm text-primary-800">
                    <strong>Note:</strong> Our care services operate 24/7. Office hours are for administrative enquiries only.
                  </p>
                </div>
              </div>

              {/* Emergency Information */}
              <div className="bg-white p-8 rounded-xl shadow-lg border border-primary-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">When to Contact Us</h3>
                <div className="space-y-4">
                  {emergencyInfo.map((info, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary-900 rounded-full mt-3"></div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{info.title}</h4>
                        <p className="text-gray-600">{info.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Location Map Placeholder */}
              <div className="bg-white p-8 rounded-xl shadow-lg border border-primary-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">HQ Location</h3>
                <div className="aspect-w-16 aspect-h-9 bg-primary-50 rounded-lg flex items-center justify-center border border-primary-100">
                  <div className="text-center">
                    <SafeIcon icon={FiMapPin} className="w-12 h-12 text-primary-400 mx-auto mb-2" />
                    <p className="text-gray-600">Interactive map would be embedded here</p>
                    <p className="text-sm text-gray-500 mt-2">86-90 Paul Street, London, EC2A 4NE</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Quick answers to common questions about our services and how to get in touch.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How quickly can care start?</h3>
                <p className="text-gray-600">Care can typically start within 48-72 hours after assessment, or immediately for emergency situations.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Do you provide care assessments?</h3>
                <p className="text-gray-600">Yes, we provide free comprehensive care assessments to understand your specific needs and preferences.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Are your carers trained and vetted?</h3>
                <p className="text-gray-600">All our carers are fully trained, DBS checked, and regularly supervised to ensure quality care delivery.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What areas do you cover?</h3>
                <p className="text-gray-600">We provide care services across London and surrounding areas. Contact us to check availability in your location.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I pay for care services?</h3>
                <p className="text-gray-600">We accept private payments, direct payments, and work with local authorities for funded care arrangements.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I speak to someone out of hours?</h3>
                <p className="text-gray-600">Yes, our 24/7 care line is always available for urgent needs and emergency situations.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
